import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Badge } from "./components/ui/badge";
import { BookOpen, ArrowLeft, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to generate slug from title (matching backend)
const generateSlug = (title) => {
  if (!title) return '';
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-+/g, '-');
};

const pillarInfo = {
  'radiant-body': {
    title: 'Radiant Body',
    description: 'Nourish your skin, optimize your weight, and fortify your immunity with alternative wellness practices that help you glow from the inside out.',
    metaDescription: 'Discover alternative wellness practices for radiant skin, healthy weight management, and strong immunity. Science-backed tips for women 40+ to glow from the inside out.',
    slug: 'radiant_body',
    prev: 'enduring-vitality',
    next: 'balanced-mind'
  },
  'balanced-mind': {
    title: 'Balanced Mind',
    description: 'Discover resources and community support to ease stress, sharpen focus, and create more calm in your daily life.',
    metaDescription: 'Find peace and mental clarity with proven stress relief techniques, focus strategies, and mindfulness practices designed for women navigating perimenopause and menopause.',
    slug: 'balanced_mind',
    prev: 'radiant-body',
    next: 'enduring-vitality'
  },
  'enduring-vitality': {
    title: 'Enduring Vitality',
    description: 'Build the strength, stamina, and resilience to keep living boldly — not just for today, but for years to come.',
    metaDescription: 'Build lasting strength, energy, and resilience with wellness strategies for women 40+. Expert guidance on maintaining vitality through every stage of life.',
    slug: 'enduring_vitality',
    prev: 'balanced-mind',
    next: 'radiant-body'
  }
};

const BlogPillarPage = () => {
  const { pillar, articleSlug } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articleLoading, setArticleLoading] = useState(false);

  const pillarData = pillarInfo[pillar];

  // Update document meta tags for SEO
  useEffect(() => {
    if (pillarData) {
      // Check if we're viewing an individual article
      if (selectedArticle) {
        // Update title for article
        document.title = `${selectedArticle.title} | ${pillarData.title} | Luminelle Wellness`;
        
        // Strip HTML from description for meta tag
        const stripHtmlForMeta = (html) => {
          const tmp = document.createElement("DIV");
          tmp.innerHTML = html;
          return (tmp.textContent || tmp.innerText || "").substring(0, 160);
        };
        
        const articleDescription = stripHtmlForMeta(selectedArticle.description);
        const articleSlugValue = selectedArticle.slug || generateSlug(selectedArticle.title);
        const articleUrl = `https://luminellewellness.com/blog/${pillar}/${articleSlugValue}`;
        
        // Update meta description
        let metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
          metaDesc.setAttribute('content', articleDescription);
        } else {
          metaDesc = document.createElement('meta');
          metaDesc.name = 'description';
          metaDesc.content = articleDescription;
          document.head.appendChild(metaDesc);
        }
        
        // Update OG tags for article
        const ogTags = {
          'og:title': `${selectedArticle.title} | Luminelle Wellness`,
          'og:description': articleDescription,
          'og:url': articleUrl,
          'og:type': 'article'
        };
        
        // Add image if available
        if (selectedArticle.image_url) {
          ogTags['og:image'] = selectedArticle.image_url;
        }
        
        Object.entries(ogTags).forEach(([property, content]) => {
          let tag = document.querySelector(`meta[property="${property}"]`);
          if (tag) {
            tag.setAttribute('content', content);
          } else {
            tag = document.createElement('meta');
            tag.setAttribute('property', property);
            tag.setAttribute('content', content);
            document.head.appendChild(tag);
          }
        });
        
        // Update canonical for article
        let canonical = document.querySelector('link[rel="canonical"]');
        if (canonical) {
          canonical.setAttribute('href', articleUrl);
        }
      } else {
        // Pillar page meta tags
        document.title = `${pillarData.title} | Luminelle Wellness - Women's Health & Wellness`;
        
        let metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
          metaDesc.setAttribute('content', pillarData.metaDescription);
        } else {
          metaDesc = document.createElement('meta');
          metaDesc.name = 'description';
          metaDesc.content = pillarData.metaDescription;
          document.head.appendChild(metaDesc);
        }
        
        const ogTags = {
          'og:title': `${pillarData.title} | Luminelle Wellness`,
          'og:description': pillarData.metaDescription,
          'og:url': `https://luminellewellness.com/blog/${pillar}`,
          'og:type': 'website'
        };
        
        Object.entries(ogTags).forEach(([property, content]) => {
          let tag = document.querySelector(`meta[property="${property}"]`);
          if (tag) {
            tag.setAttribute('content', content);
          } else {
            tag = document.createElement('meta');
            tag.setAttribute('property', property);
            tag.setAttribute('content', content);
            document.head.appendChild(tag);
          }
        });
        
        let canonical = document.querySelector('link[rel="canonical"]');
        if (canonical) {
          canonical.setAttribute('href', `https://luminellewellness.com/blog/${pillar}`);
        }
      }
    }
  }, [pillar, pillarData, selectedArticle]);

  useEffect(() => {
    if (!pillarData) {
      navigate('/');
      return;
    }
    fetchContent();
  }, [pillar]);

  // Fix bullet lists in displayed content
  useEffect(() => {
    const fixBulletLists = () => {
      // Target both UL (new format) and ol[data-list="bullet"] (legacy)
      const bulletLists = document.querySelectorAll('.rich-text-content ul, .prose ul, .rich-text-content ol[data-list="bullet"], .prose ol[data-list="bullet"]');
      bulletLists.forEach(list => {
        list.style.listStyleType = 'disc';
        list.style.listStylePosition = 'outside';
        list.style.paddingLeft = '1.5em';
        list.style.counterReset = 'none';
        list.querySelectorAll('li').forEach(li => {
          li.style.listStyleType = 'disc';
          li.style.counterIncrement = 'none';
        });
      });
    };
    
    if (selectedArticle) {
      setTimeout(fixBulletLists, 100);
      // Track public article view
      try {
        axios.post(`${API}/track/public-view`, {
          content_type: 'article',
          content_id: selectedArticle.id || selectedArticle._id,
          content_title: selectedArticle.title
        });
      } catch (e) { /* silent */ }
    }
  }, [selectedArticle]);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/blog/public`, {
        params: { pillar: pillarData.slug }
      });
      setContent(response.data || []);
    } catch (error) {
      console.error('Error fetching blog content:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load article from URL slug if present
  useEffect(() => {
    const loadArticleFromSlug = async () => {
      if (articleSlug && pillar) {
        setArticleLoading(true);
        try {
          const response = await axios.get(`${API}/blog/${pillar}/${articleSlug}`);
          if (response.data) {
            setSelectedArticle(response.data);
            window.scrollTo(0, 0);
          }
        } catch (error) {
          console.error('Error loading article:', error);
          // Article not found, navigate back to pillar page
          navigate(`/blog/${pillar}`, { replace: true });
        } finally {
          setArticleLoading(false);
        }
      } else if (!articleSlug && selectedArticle) {
        // URL changed to pillar-only, clear selected article
        setSelectedArticle(null);
      }
    };

    loadArticleFromSlug();
  }, [articleSlug, pillar]);

  const handleContentClick = (item) => {
    // Navigate to article URL with slug
    const slug = item.slug || generateSlug(item.title);
    navigate(`/blog/${pillar}/${slug}`);
  };

  const handleBackToPillar = () => {
    // Navigate back to pillar page (clears articleSlug from URL)
    setSelectedArticle(null);
    navigate(`/blog/${pillar}`);
  };
  
  // Strip HTML tags for preview text
  const stripHtml = (html) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  if (!pillarData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-burgundy text-cream py-4 px-6 shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <a href="/" className="cursor-pointer">
              <img src="/assets/luminelle-logo.png" alt="Luminelle" className="h-10 hover:opacity-80 transition-opacity" />
            </a>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Pillar Navigation */}
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate(`/blog/${pillarData.prev}`)}
              className="text-cream hover:bg-cream/10"
              title={`Previous: ${pillarInfo[pillarData.prev].title}`}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <span className="text-cream/70 text-sm hidden sm:inline">{pillarData.title}</span>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate(`/blog/${pillarData.next}`)}
              className="text-cream hover:bg-cream/10"
              title={`Next: ${pillarInfo[pillarData.next].title}`}
            >
              <ArrowLeft className="h-4 w-4 rotate-180" />
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Loading state for direct article URL access */}
        {articleLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="h-10 w-10 text-burgundy animate-spin mb-4" />
            <p className="text-burgundy/70">Loading article...</p>
          </div>
        ) : selectedArticle ? (
          <div className="max-w-4xl mx-auto">
            <Button
              variant="ghost"
              onClick={handleBackToPillar}
              className="mb-6 text-burgundy hover:text-burgundy/80"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to {pillarData.title}
            </Button>
            
            <Card className="bg-white border-burgundy/20">
              <CardHeader>
                <CardTitle className="text-3xl md:text-4xl font-serif text-burgundy mb-4">
                  {selectedArticle.title}
                </CardTitle>
                <div 
                  className="text-burgundy/80 text-lg"
                  dangerouslySetInnerHTML={{ __html: selectedArticle.description }}
                />
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 mb-6">
                  {selectedArticle.tags?.map((tag, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs bg-burgundy/10 text-burgundy">
                      {tag}
                    </Badge>
                  ))}
                </div>
                
                {selectedArticle.image_url && (
                  <div className="mb-6">
                    <img 
                      src={selectedArticle.image_url} 
                      alt={selectedArticle.image_alt || selectedArticle.title}
                      className="w-full max-w-2xl mx-auto rounded-lg shadow-lg"
                      loading="lazy"
                    />
                  </div>
                )}
                
                <div 
                  className="rich-text-content prose prose-lg max-w-none text-gray-800 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: selectedArticle.content || 'No content available.' }}
                />

                {/* CTA in Article */}
                <div className="mt-12 pt-8 border-t border-burgundy/20 text-center">
                  <p className="text-burgundy/70 mb-6">
                    Sign up for a free trial and get full access to our expert resources, tools and community discussion
                  </p>
                  <a href="/#signup">
                    <button className="bg-gradient-to-r from-gold to-gold-600 hover:from-gold-600 hover:to-gold-700 text-burgundy font-semibold px-8 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 border border-gold-300">
                      Begin Your Journey
                    </button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <>
            {/* Pillar Header */}
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-burgundy font-serif mb-4">
                {pillarData.title}
              </h1>
              <p className="text-lg text-burgundy/70 max-w-3xl mx-auto">
                {pillarData.description}
              </p>
            </div>

        {/* Content Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burgundy"></div>
            <span className="ml-2 text-burgundy">Loading content...</span>
          </div>
        ) : content.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="h-16 w-16 text-burgundy/30 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-burgundy/60 mb-2 font-serif">No Content Available</h3>
            <p className="text-burgundy/50">Content for this pillar will be published soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {content.map((item) => (
              <Card 
                key={item.id} 
                className="hover:shadow-lg transition-all duration-300 bg-white border-burgundy/20 cursor-pointer overflow-hidden group"
                onClick={() => handleContentClick(item)}
              >
                {/* Thumbnail Image */}
                {item.image_url && (
                  <div className="relative h-48 overflow-hidden bg-gray-100">
                    <img 
                      src={item.image_url} 
                      alt={item.image_alt || item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <Badge 
                      className="absolute top-2 right-2 bg-burgundy text-cream"
                    >
                      {item.content_type}
                    </Badge>
                  </div>
                )}

                <CardHeader>
                  <CardTitle className="text-lg font-serif text-burgundy group-hover:text-burgundy/80 transition-colors">
                    {item.title}
                  </CardTitle>
                </CardHeader>

                <CardContent>
                  <p className="text-burgundy/70 text-sm mb-4 line-clamp-3">
                    {stripHtml(item.description || '')}
                  </p>
                  
                  <div className="flex flex-wrap gap-2 mb-3">
                    {item.tags?.slice(0, 3).map((tag, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs border-burgundy/30 text-burgundy">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  
                  <p className="text-xs text-burgundy/50 italic">
                    Click to read article
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

            {/* Bottom CTA */}
            {!loading && content.length > 0 && (
              <div className="text-center mt-16 py-8 border-t border-burgundy/20">
                <p className="text-burgundy/70 mb-4">
                  Sign up for a free trial and get full access to our expert resources, tools and community discussion
                </p>
                <a href="/#signup">
                  <button className="bg-gradient-to-r from-gold to-gold-600 hover:from-gold-600 hover:to-gold-700 text-burgundy font-semibold px-8 py-2 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 border border-gold-300">
                    Begin Your Journey
                  </button>
                </a>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BlogPillarPage;
